import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import DocumentsForReviewCSVQuery from "./graphql/DocumentsForReviewCSVQuery";
import useDocumentsToReveiwReport from "./hooks";
import { buildPolicyTypesForSelector, shouldShowOtherReports } from "./utils";

import { SearchBarAndFilters } from "pstat-anywhere/components/document_reports/expiring/DocumentsForReviewFilters";
import {
  AdvancedReportPage,
  DefaultActionPanel
} from "pstat-anywhere/components/pages/reports";
import { PolicyTypeSelector } from "pstat-anywhere/components/pages/reports/components/PolicyTypeSelector";
import { Bucket } from "pstat-anywhere/components/reports/Buckets";
import { withLabelContext } from "pstat-anywhere/context_providers/LabelContext";
import { useAdoptedMapping } from "pstat-anywhere/utils/hooks/useAdoptedMapping";
import { InfoBanner } from "pstat-design-system/banner";


export const Buckets = ({ bucketsConfig }) => {
  const bucketItems = bucketsConfig.map(({ label, count, color, key }) => (
    <Bucket label={label} count={count} color={color} key={key} />
  ));
  return <Fragment>{bucketItems}</Fragment>;
};

export const getBucketsConfig = (t, isTemplateView, buckets, route) => {
  if (route?.data?.routeName === "expiring_templates_rejected") {
    return [
      {
        key: 0,
        label: t("documentReports.waitingLessThanBucket", { count: 30 }),
        count: buckets?.["30"],
        color: "buckets.ok"
      },
      {
        key: 1,
        label: t("documentReports.waitingLessThanBucket", { count: 60 }),
        count: buckets?.["60"],
        color: "buckets.warning"
      },
      {
        key: 2,
        label: t("documentReports.waitingLessThanBucket", { count: 90 }),
        count: buckets?.["90"],
        color: "buckets.warning"
      },
      {
        key: 3,
        label: t("documentReports.waitingGreaterThanBucket", { count: 90 }),
        count: buckets?.["old"],
        color: "buckets.danger"
      }
    ];
  }
  if (isTemplateView) {
    return [
      {
        key: 0,
        label: t("documentReports.waitingLessThanBucket", { count: 30 }),
        count: buckets?.["30"],
        color: "buckets.ok"
      },
      {
        key: 1,
        label: t("documentReports.waitingLessThanBucket", { count: 60 }),
        count: buckets?.["60"],
        color: "buckets.warning"
      },
      {
        key: 2,
        label: t("documentReports.waitingLessThanBucket", { count: 90 }),
        count: buckets?.["90"],
        color: "buckets.warning"
      },
      {
        key: 3,
        label: t("documentReports.waitingGreaterThanBucket", { count: 90 }),
        count: buckets?.["old"],
        color: "buckets.danger"
      }
    ];
  }
  const lastStepKey = buckets ? Object.keys(buckets)[3] : null;

  return [
    {
      key: 0,
      label: t("documentReports.pastDue"),
      count: buckets?.["0"],
      color: "buckets.danger"
    },
    {
      key: 1,
      label: t("documentReports.lessThanBucket", { count: 30 }),
      count: buckets?.["30"],
      color: "buckets.warning"
    },
    {
      key: 2,
      label: t("documentReports.lessThanBucket", { count: 60 }),
      count: buckets?.["60"],
      color: "buckets.ok"
    },
    {
      key: 3,
      label: t("documentReports.lessThanBucket", {
        count: lastStepKey ? lastStepKey : ""
      }),
      count: buckets?.[lastStepKey],
      color: "buckets.ok"
    },
    {
      key: 4,
      label: t("documentReports.greaterThanBucket", {
        count: lastStepKey ? lastStepKey : ""
      }),
      count: buckets?.not_expiring,
      color: "buckets.neutral"
    }
  ];
};

function DocumentsToReviewReport({
  documentsToReview,
  route,
  router,
  location,
  labels
}) {
  const { t } = useTranslation();
  const {
    variables,
    handleBulkReview,
    loading,
    isTemplateView
  } = useDocumentsToReveiwReport({
    location,
    router,
    route,
    labels,
    t
  });
  const {
    justMine,
    includePending,
    categories: categoriesArg,
    daysUntilExpiration,
    reviewType
  } = variables;
  const baseUrl = isTemplateView
    ? "policy_reports/expiring/template"
    : "policy_reports/expiring";
  const tabs = [
    {
      id: isTemplateView ? "expiring_templates_all" : "expiring_all",
      textKey: "documentReports.expiring.tabs.everyone",
      path: baseUrl
    },
    {
      id: isTemplateView ? "expiring_templates_mine" : "expiring_mine",
      textKey: "documentReports.expiring.tabs.mine",
      path: `${baseUrl}/mine`
    }
  ];
  const rejectedTemplatesTabs = [
    {
      id: "rejected_templates_all",
      textKey: "documentReports.expiring.tabs.all"
    }
  ];
  const isCheckboxDisabled = rowData => {
    if (rowData.cannotBulkReviewReason === "HAS_DRAFT") {
      return "documentReports.expiring.cannotBulkReview.hasDraft";
    }
    if (rowData.cannotBulkReviewReason === "INACTIVE_AUTHOR") {
      return "documentReports.expiring.cannotBulkReview.inactiveAuthor";
    }
    if (rowData.cannotBulkReviewReason === "SPECIFIC_EXPIRATION_DATE") {
      return "documentReports.expiring.cannotBulkReview.specificExpirationDate";
    }
  };
  const getExpiringTemplatesTable = () => "expiring_templates";
  const getExpiringDocumentsTable = () =>
    includePending ? "expiring_with_pending" : "expiring";
  const getExpringDocumentsWithBulReviewTable = () =>
    includePending
      ? "expiring_with_bulk_review_pending"
      : "expiring_with_bulk_review";

  const getExpiringTable = showBulkReview => {
    if (route?.data?.routeName === "expiring_templates_rejected") {
      return "rejected_templates";
    }
    if (isTemplateView) {
      return getExpiringTemplatesTable();
    }
    if (showBulkReview) {
      return getExpringDocumentsWithBulReviewTable();
    }
    return getExpiringDocumentsTable();
  };
  const {
    adoptedMapping,
    onChangeAdoptedDocument,
    constructDocumentsOfTemplate
  } = useAdoptedMapping();

  const tableType = getExpiringTable(documentsToReview?.showBulkReview);
  const { buckets, categories, documents, rssFeedUrl, counts } =
    documentsToReview || {};
  const {
    showTemplatesReport,
    showRejectedTemplatesReport
  } = shouldShowOtherReports(counts);
  const headerText =
    showTemplatesReport || showRejectedTemplatesReport
      ? t("documentReports.expiring.headerWithoutTypeDocuments")
      : t("documentReports.expiring.header", {
          documentLabelPlural: labels.documentLabelPlural
        });
  const policyTypes = buildPolicyTypesForSelector({
    counts,
    documentLabelPlural: labels.documentLabelPlural,
    t
  });
  const isRejectedTemplateView =
    route?.data?.routeName === "expiring_templates_rejected";
  let currentPolicyTypeTitle = null;
  if (isTemplateView) {
    currentPolicyTypeTitle = t(
      "documentReports.expiring.documentTypeSelector.templateTitle",
      { documentLabelPlural: labels.documentLabelPlural }
    );
  } else if (isRejectedTemplateView) {
    currentPolicyTypeTitle = t(
      "documentReports.expiring.documentTypeSelector.rejectTemplateTitle",
      { documentLabelPlural: labels.documentLabelPlural }
    );
  } else {
    currentPolicyTypeTitle = labels.documentLabelPlural;
  }
  const hasTemplateInDocuments =
    documents &&
    documents.edges
      .map(({ node }) => node.isTemplate)
      .some(isTemplate => isTemplate);
  const PrefixHeaderComponent =
    showTemplatesReport || showRejectedTemplatesReport
      ? () => (
          <PolicyTypeSelector
            policyTypes={policyTypes}
            title={currentPolicyTypeTitle}
          />
        )
      : null;

  const buildPoliciesForRendering = documents => {
    if (documents && documents.edges) {
      return {
        ...documents,
        edges: documents.edges.map(({ node }) => ({
          ...{
            node: {
              ...(adoptedMapping[node.pk] || node),
              originPk: node.pk,
              onChangeAdoptedDocument,
              documentsOfTemplate: constructDocumentsOfTemplate(node)
            }
          }
        }))
      };
    }
    return documents;
  };

  return (
    <Fragment>
      {isTemplateView && hasTemplateInDocuments && (
        <InfoBanner
          message={t("documentReports.expiring.titleView.templateInfoBanner", {
            documentLabelPlural: labels.documentLabelPlural.toLowerCase()
          })}
          width="100%"
        />
      )}
      <AdvancedReportPage
        csvQueryName="documentsToReviewCsv"
        csvQueryArgs={{
          justMine,
          includePending,
          categories: categoriesArg,
          daysUntilExpiration,
          reviewType
        }}
        CSVQuery={DocumentsForReviewCSVQuery}
        variables={variables}
        data={buildPoliciesForRendering(documents)}
        route={route}
        router={router}
        location={location}
        tabs={isRejectedTemplateView ? rejectedTemplatesTabs : tabs}
        headerText={headerText}
        noContentMessage={t(
          "documentReports.expiring.reportContent.noContentMessage",
          {
            documentLabelPlural: labels.documentLabelPlural.toLowerCase()
          }
        )}
        showActionButtonPanel={true}
        showTabsPanel={true}
        tableType={tableType}
        renderActionPanel={selectedPolicies => (
          <DefaultActionPanel
            selectedItemPks={selectedPolicies}
            actionTextKey={"documentReports.expiring.pendForApprovalButton"}
            onActionButtonClick={handleBulkReview}
            loading={loading}
          />
        )}
        BucketsComponent={() => (
          <Buckets
            bucketsConfig={getBucketsConfig(t, isTemplateView, buckets, route)}
          />
        )}
        otherTableProps={{ isCheckboxDisabled }}
        isCheckboxDisabled={isCheckboxDisabled}
        rssFeedUrl={rssFeedUrl}
        renderFilterPanel={() => {
          if (isRejectedTemplateView) {
            return null;
          }
          return (
            <SearchBarAndFilters
              location={location}
              router={router}
              categories={categories}
              waitingSinceFilter={isTemplateView}
            />
          );
        }}
        PrefixHeaderComponent={PrefixHeaderComponent}
      />
    </Fragment>
  );
}

export default withLabelContext(DocumentsToReviewReport);
